// DistributionSummaryView — the "Distribution" view: a value histogram for a
// single numeric feature, the summary-card analogue of the standalone
// HistogramBody. Answers "what's typical?" rather than "how's it trending?".
//
// Stateless: the card owns the fetch (this view declares the 'timeseries'
// source) and hands the raw, ms-dated series in via `timeseries`. This body
// slices it to the active window and defers all binning + bar rendering to the
// shared HistogramChart core, so it bins identically to the standalone chart.

import React, { useMemo } from 'react'
import HistogramChart from '../../../charts/bodies/HistogramChart'
import type { SummaryViewBodyProps } from '../viewRegistry'

const DistributionSummaryView: React.FC<SummaryViewBodyProps> = ({ unit, window, timeseries = [] }) => {
  // Slice to the active window ([start, end), half-open). 'All' has non-finite
  // bounds — bin every point.
  const data = useMemo(() => {
    const { start, end } = window
    const bounded = Number.isFinite(start) && Number.isFinite(end)
    return bounded ? timeseries.filter((p) => p.date >= start && p.date < end) : timeseries
  }, [timeseries, window])

  // NOTE: the feature's value-type config isn't threaded through the summary
  // card yet, so HistogramChart falls back to its integer/decimal heuristic
  // (no rating-label bins). Pass `config` here once the card carries it.
  return (
    <div data-testid="summary-distribution-view" data-point-count={data.length}>
      <div className="h-64">
        <HistogramChart data={data} unit={unit} />
      </div>
    </div>
  )
}

export default DistributionSummaryView
